import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllTeamMembers,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
  TEAM_CATEGORIES,
} from "../services/azureTeamService";
import { getEmployees } from "../services/apiService";
import { getAllProjectsWithTags } from "../services/azureProjectsService";
import {
  uploadTeamImage,
  deleteTeamImage,
} from "../services/azureTeamBlobService";
import { hasRole, ROLES } from "../utils/permissions";
import {
  getCurrentSalary,
  deleteAllSalaryEntries,
  getAllPkManualEntries,
} from "../services/azureSalaryService";
import { getPkManualUpdateReasons } from "../utils/insuranceCalculations";
import {
  createOnboardingBudgetEntry,
  deleteMemberBudgetEntries,
  endMemberSalaryBudgets,
  renameMemberBudgetEntries,
  recalculateInsuranceBudget,
  recalculateFAKBudget,
} from "../services/azureBudgetService";
import { getAllActiveRates, getActiveRates } from "../services/azureInsuranceService";
import TeamMemberModal from "./TeamMemberModal";
import TeamDetailModal from "./TeamDetailModal";
import HrSettingsModal from "./HrSettingsModal";
import InsuranceSettingsModal from "./InsuranceSettingsModal";

const TeamManagement = ({ user }) => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]); // For freelancer project assignment
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [showFormer, setShowFormer] = useState(false);

  // Modal state
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Image expand state
  const [expandedImageId, setExpandedImageId] = useState(null);

  // HR modal state
  const [showHrModal, setShowHrModal] = useState(false);

  // Insurance modal state
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);

  // Salary data for GL view (teamMemberId -> current salary amount)
  const [salaryMap, setSalaryMap] = useState({});

  // Full current salary entries (employeeUuid -> salary object), needed for PK status
  const [currentSalaries, setCurrentSalaries] = useState({});

  // Manual PK mode: members needing an update (teamId -> reasons[])
  const [pkUpdateMap, setPkUpdateMap] = useState({});

  // Column reveal state for sensitive values (hover on header reveals all values)
  const [revealColumn, setRevealColumn] = useState(null); // "rate" | "lohn" | null

  // GL category maps to MF-Admin access rights (no separate MF-GL role exists)
  const isAdmin = hasRole(user, ROLES.ADMIN);
  const isPL = hasRole(user, ROLES.PL);
  const canManageTeam = isAdmin;
  const canRecalcInsurance = isAdmin || isPL;

  // Sozialabgaben recalculation state
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcMessage, setRecalcMessage] = useState(null); // { type: "success"|"error", text: "..." }

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [team, projectsList] = await Promise.all([
        getAllTeamMembers(),
        getAllProjectsWithTags("project"), // Load projects for freelancer assignment
      ]);
      setTeamMembers(team);
      setProjects(projectsList);

      // Load AbaNinja employees separately — non-critical, may 404 for non-AbaNinja users
      try {
        const emps = await getEmployees();
        setEmployees(emps);
      } catch {
        // AbaNinja not available for this user — employee list stays empty
      }

      // Load salary data for GL view
      if (isAdmin) {
        loadSalaryMap(team);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSalaryMap = async (members) => {
    try {
      const salaryPromises = members.map(async (m) => {
        const salary = await getCurrentSalary(m.employeeUuid);
        return { employeeUuid: m.employeeUuid, salary };
      });
      const results = await Promise.all(salaryPromises);
      const map = {};
      const salaryObjMap = {};
      results.forEach(({ employeeUuid, salary }) => {
        if (salary && employeeUuid) {
          // Use brutto for insurance calc basis (pensum-adjusted)
          const lohn = salary.brutto || salary.netto || 0;
          const pensum = salary.pensum ?? 100;
          map[employeeUuid] = (lohn * pensum) / 100;
          salaryObjMap[employeeUuid] = salary;
        }
      });
      setSalaryMap(map);
      setCurrentSalaries(salaryObjMap);
      loadPkStatus(members, salaryObjMap);
    } catch {
      // Salary map loading errors silenced per security
    }
  };

  // Manual PK mode: determine which active members need their PK contributions
  // (re-)confirmed — Pensumänderung, Neueintritt, Jahreswechsel, Lohnänderung
  const loadPkStatus = async (members, salaries) => {
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      const pkRate = await getActiveRates("pk", todayStr);
      if (!pkRate || pkRate.berechnungsModus !== "manuell") {
        setPkUpdateMap({});
        return;
      }
      const entries = await getAllPkManualEntries();
      const entryMap = {};
      entries.forEach((e) => { entryMap[e.teamMemberId] = e; });

      const currentYear = new Date().getFullYear();
      const map = {};
      members.forEach((m) => {
        // Only active members with a current salary entry need PK contributions
        if (!m.start || m.start > todayStr) return;
        if (m.ende && m.ende < todayStr) return;
        const salary = salaries[m.employeeUuid];
        if (!salary) return;

        const effPensum = salary.pensum ?? m.pensum ?? 100;
        const reasons = getPkManualUpdateReasons(entryMap[m.employeeUuid], {
          jahr: currentYear,
          pensum: effPensum,
          salaryId: salary.id,
        });
        if (reasons.length > 0) map[m.teamId] = reasons;
      });
      setPkUpdateMap(map);
    } catch {
      // PK status loading errors silenced per security
    }
  };

  const handleRecalcInsurance = async () => {
    setRecalcLoading(true);
    setRecalcMessage(null);
    try {
      // Refresh salary map to ensure latest data
      const salaryPromises = teamMembers.map(async (m) => {
        const salary = await getCurrentSalary(m.employeeUuid);
        return { employeeUuid: m.employeeUuid, salary };
      });
      const results = await Promise.all(salaryPromises);
      const currentSalaryMap = {};
      results.forEach(({ employeeUuid, salary }) => {
        if (salary && employeeUuid) {
          const lohn = salary.brutto || salary.netto || 0;
          const pensum = salary.pensum ?? 100;
          currentSalaryMap[employeeUuid] = (lohn * pensum) / 100;
        }
      });
      setSalaryMap(currentSalaryMap);

      const today = new Date().toISOString().split("T")[0];
      const rates = await getAllActiveRates(today);
      if (!rates) throw new Error("Keine Versicherungssätze gefunden");

      await Promise.all([
        recalculateInsuranceBudget(rates),
        recalculateFAKBudget(rates.fak),
      ]);
      setRecalcMessage({ type: "success", text: "Sozialabgaben wurden erfolgreich aktualisiert." });
    } catch {
      setRecalcMessage({ type: "error", text: "Fehler beim Aktualisieren der Sozialabgaben. Bitte erneut versuchen." });
    } finally {
      setRecalcLoading(false);
    }
  };

  // Filter and search logic
  const filteredMembers = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];

    return teamMembers.filter((member) => {
      // Filter by employment status (default: currently employed)
      if (!showFormer) {
        const isCurrentlyEmployed =
          member.start <= today && (!member.ende || member.ende >= today);
        if (!isCurrentlyEmployed) return false;
      }

      // Filter by category
      if (filterCategory && member.kategorie !== filterCategory) return false;

      // Search by name or nummer
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = member.name?.toLowerCase().includes(query);
        const matchesNummer = member.nummer?.toLowerCase().includes(query);
        const matchesKategorie = member.kategorie?.toLowerCase().includes(query);
        if (!matchesName && !matchesNummer && !matchesKategorie) return false;
      }

      return true;
    });
  }, [teamMembers, searchQuery, filterCategory, showFormer]);

  const handleSaveMember = async (memberData, isNew) => {
    setLoading(true);
    setError(null);
    try {
      if (isNew) {
        const memberWithoutImage = {
          employeeUuid: memberData.employeeUuid,
          nummer: memberData.nummer,
          name: memberData.name,
          email: memberData.email,
          internalRate: memberData.internalRate,
          pensum: memberData.pensum,
          start: memberData.start,
          ende: memberData.ende,
          beschrieb: memberData.beschrieb,
          public: memberData.public,
          imTeam: memberData.imTeam,
          kategorie: memberData.kategorie,
          geschlecht: memberData.geschlecht || "",
          geburtsdatum: memberData.geburtsdatum || "",
          kinder: memberData.kinder || [],
          bildId: "",
          bildUrls: null,
        };

        const result = await createTeamMember(memberWithoutImage);
        const teamId = result.teamId;
        const employeeUuid = result.employeeUuid;

        let finalMember = { ...result };
        if (memberData.tempFile) {
          try {
            const { bildId, urls } = await uploadTeamImage(
              teamId,
              memberData.tempFile
            );
            await updateTeamMember(teamId, {
              ...result,
              bildId,
              bildUrls: urls,
            });
            finalMember = { ...result, bildId, bildUrls: urls };
          } catch (imgErr) {
            console.error("Bild-Upload fehlgeschlagen:", imgErr);
            setError("Member erstellt, aber Bild-Upload fehlgeschlagen");
          }
        }

        // Create onboarding budget entries if selected
        const onboardingDate = (() => {
          if (!memberData.start) return "";
          const d = new Date(memberData.start);
          d.setDate(d.getDate() - 14);
          return d.toISOString().split("T")[0];
        })();

        if (memberData.onboardingLaptop && memberData.onboardingLaptopBetrag) {
          try {
            await createOnboardingBudgetEntry({
              teamMemberId: employeeUuid,
              memberName: memberData.name,
              itemName: "Laptop",
              betrag: parseFloat(memberData.onboardingLaptopBetrag),
              datum: onboardingDate,
            });
          } catch (err) {
            console.error("Onboarding-Budget (Laptop) fehlgeschlagen:", err);
          }
        }

        if (memberData.onboardingArchicad && memberData.onboardingArchicadBetrag) {
          try {
            await createOnboardingBudgetEntry({
              teamMemberId: employeeUuid,
              memberName: memberData.name,
              itemName: "ArchiCad Lizenz",
              betrag: parseFloat(memberData.onboardingArchicadBetrag),
              datum: onboardingDate,
            });
          } catch (err) {
            console.error("Onboarding-Budget (ArchiCad) fehlgeschlagen:", err);
          }
        }

        setTeamMembers([...teamMembers, finalMember]);
      } else {
        // Update existing member
        const teamId = memberData.teamId;

        // Check if end date was set (employment ending)
        const existingMember = teamMembers.find((m) => m.teamId === teamId);
        const endeDateChanged = memberData.ende && memberData.ende !== existingMember?.ende;
        const nameChanged =
          memberData.name &&
          existingMember?.name &&
          memberData.name !== existingMember.name;

        // Handle new image upload
        let updatedData = { ...memberData };
        if (memberData.tempFile) {
          try {
            const { bildId, urls } = await uploadTeamImage(
              teamId,
              memberData.tempFile
            );
            updatedData.bildId = bildId;
            updatedData.bildUrls = urls;
          } catch (imgErr) {
            console.error("Bild-Upload fehlgeschlagen:", imgErr);
            setError("Bild-Upload fehlgeschlagen");
          }
        }

        // Remove transient fields before saving
        delete updatedData.tempFile;
        delete updatedData.onboardingLaptop;
        delete updatedData.onboardingLaptopBetrag;
        delete updatedData.onboardingArchicad;
        delete updatedData.onboardingArchicadBetrag;

        await updateTeamMember(teamId, updatedData);

        // If end date was set, close all active salary budget entries
        if (endeDateChanged) {
          try {
            await endMemberSalaryBudgets(
              existingMember?.employeeUuid || memberData.employeeUuid,
              memberData.ende,
            );
          } catch (err) {
            console.error("Lohn-Budget Enddatum setzen fehlgeschlagen:", err);
          }
        }

        // Umbenennung: Namen der verknüpften Budget-Einträge nachziehen
        // (Lohn/13. ML/Onboarding inkl. Monats-Kopien tragen sonst dauerhaft
        // den alten Namen — sie werden nur bei der Erstellung benannt)
        if (nameChanged) {
          try {
            await renameMemberBudgetEntries(
              existingMember?.employeeUuid || memberData.employeeUuid,
              existingMember.name,
              memberData.name,
            );
          } catch (err) {
            console.error("Budget-Namen aktualisieren fehlgeschlagen:", err);
          }
        }

        setTeamMembers(
          teamMembers.map((m) => (m.teamId === teamId ? updatedData : m))
        );
      }

      // Recalculate insurance budgets after member changes (fire-and-forget)
      if (isAdmin) {
        getAllActiveRates(new Date().toISOString().split("T")[0]).then((rates) => {
          if (rates) {
            recalculateInsuranceBudget(rates).catch(() => {});
            recalculateFAKBudget(rates.fak).catch(() => {});
          }
        }).catch(() => {});
      }

      setShowMemberModal(false);
      setEditingMember(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (teamId) => {
    if (!confirm("Teammitglied wirklich löschen?\n\nAlle verknüpften Budget-Einträge (Lohn, Onboarding) werden entfernt.")) return;

    setLoading(true);
    try {
      const member = teamMembers.find((m) => m.teamId === teamId);
      const employeeUuid = member?.employeeUuid;

      // Delete linked budget entries (salary, onboarding) -- referenziert via employeeUuid
      if (employeeUuid) {
        try {
          await deleteMemberBudgetEntries(employeeUuid);
        } catch (err) {
          console.error("Budget-Einträge löschen fehlgeschlagen:", err);
        }

        // Delete all salary entries
        try {
          await deleteAllSalaryEntries(employeeUuid);
        } catch (err) {
          // Salary deletion errors silenced per security
        }
      }

      // Delete the team member
      await deleteTeamMember(teamId);
      setTeamMembers(teamMembers.filter((m) => m.teamId !== teamId));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const canViewMember = (member) => {
    if (canManageTeam) return true;
    const currentUserMember = teamMembers.find(
      (m) => m.employeeUuid === user?.id || m.name === user?.name
    );
    return currentUserMember?.teamId === member.teamId;
  };

  const handleRowClick = (member) => {
    if (!canViewMember(member)) return;
    setSelectedMember(member);
    setShowDetailModal(true);
  };

  const handleOpenAuswertung = (member, e) => {
    e.stopPropagation();
    if (!canViewMember(member)) return;
    navigate(`/team/${member.teamId}/auswertung`);
  };

  const handleOpenOwnProfile = () => {
    // For non-admin users clicking the "Team" tag
    const currentUserMember = teamMembers.find(
      (m) => m.employeeUuid === user?.id || m.name === user?.name
    );
    if (currentUserMember) {
      setSelectedMember(currentUserMember);
      setShowDetailModal(true);
    }
  };

  const getCategoryBadgeClass = (kategorie) => {
    switch (kategorie) {
      case "GL": return "badge-gl";
      case "PL": return "badge-pl";
      case "Architekt:in": return "badge-arch";
      case "Praktikant:in": return "badge-prakt";
      case "Freelancer": return "badge-freelancer";
      default: return "badge-default";
    }
  };

  return (
    <div className="team-management">
      <div className="page-header">
        <h1>Team Malheur & Fortuna</h1>
        <div className="header-actions">
          {canRecalcInsurance && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleRecalcInsurance}
              disabled={recalcLoading}
              title="Sozialabgaben im Budget neu berechnen"
            >
              {recalcLoading ? "Wird berechnet..." : "Sozialabgaben aktualisieren"}
            </button>
          )}
          {canManageTeam && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowInsuranceModal(true)}
              title="Versicherungen verwalten"
            >
              Versicherungen
            </button>
          )}
          {canManageTeam && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowHrModal(true)}
              title="HR Einstellungen"
            >
              HR
            </button>
          )}
          {!canManageTeam && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleOpenOwnProfile}
              title="Eigene Daten anzeigen"
            >
              Meine Daten
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {recalcMessage && (
        <div
          className={recalcMessage.type === "success" ? "success-message" : "error-message"}
          onClick={() => setRecalcMessage(null)}
        >
          {recalcMessage.text}
        </div>
      )}

      {/* Manual PK mode: banner for GL when contributions are missing or stale */}
      {isAdmin && Object.keys(pkUpdateMap).length > 0 && (
        <div className="pk-update-banner">
          Pensionskasse: {Object.keys(pkUpdateMap).length}{" "}
          {Object.keys(pkUpdateMap).length === 1 ? "Person muss" : "Personen müssen"} aktualisiert
          werden (manuelle PK-Beiträge fehlen oder sind veraltet). Betroffene Personen sind in der
          Liste markiert — Beiträge im Personendetail unter "Pensionskasse (manuell)" bestätigen.
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="team-controls">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Name, Nummer oder Kategorie suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          <select
            className="filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">Alle Kategorien</option>
            {TEAM_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          {canManageTeam && (
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={showFormer}
                onChange={(e) => setShowFormer(e.target.checked)}
              />
              Inaktive anzeigen
            </label>
          )}
        </div>
        <div className="table-info">
          {filteredMembers.length} von {teamMembers.length} Mitgliedern
        </div>
      </div>

      {/* Team Table */}
      <div className="team-table-container">
        <table className={`team-table team-table-compact${revealColumn === "rate" ? " reveal-rate" : ""}${revealColumn === "lohn" ? " reveal-lohn" : ""}`}>
          <thead>
            <tr>
              <th>Nr.</th>
              <th>Name</th>
              <th>Kategorie</th>
              <th>Pensum</th>
              <th>Start</th>
              <th>Ende</th>
              {canManageTeam && <th onMouseEnter={() => setRevealColumn("rate")} onMouseLeave={() => setRevealColumn(null)}>Rate</th>}
              {canManageTeam && <th onMouseEnter={() => setRevealColumn("lohn")} onMouseLeave={() => setRevealColumn(null)}>Lohn</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((member) => (
              <tr
                key={member.teamId}
                className={`team-row ${canManageTeam || teamMembers.find(m => m.employeeUuid === user?.id || m.name === user?.name)?.teamId === member.teamId ? "clickable-row" : ""}`}
                onClick={() => handleRowClick(member)}
              >
                <td className="cell-nummer">
                  <div className="nummer-with-image">
                    {member.bildUrls?.thumbnail && (
                      <span
                        className="image-toggle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedImageId(
                            expandedImageId === member.teamId ? null : member.teamId
                          );
                        }}
                        title="Bild anzeigen"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21,15 16,10 5,21"/>
                        </svg>
                      </span>
                    )}
                    <span>{member.nummer || "-"}</span>
                  </div>
                  {expandedImageId === member.teamId && member.bildUrls?.thumbnail && (
                    <div className="expanded-image" onClick={(e) => e.stopPropagation()}>
                      <img
                        src={member.bildUrls.medium || member.bildUrls.thumbnail}
                        alt={member.name}
                      />
                    </div>
                  )}
                </td>
                <td className="cell-name">
                  {member.name}
                  {isAdmin && pkUpdateMap[member.teamId] && (
                    <span
                      className="pk-update-dot"
                      title={`Pensionskasse aktualisieren: ${pkUpdateMap[member.teamId].join(", ")}`}
                    ></span>
                  )}
                </td>
                <td>
                  {member.kategorie && (
                    <span className={`category-badge ${getCategoryBadgeClass(member.kategorie)}`}>
                      {member.kategorie}
                    </span>
                  )}
                </td>
                <td>{member.pensum ?? 100}%</td>
                <td>{member.start || "-"}</td>
                <td>{member.ende || "-"}</td>
                {canManageTeam && <td className="cell-rate"><span className="sensitive-bar">{member.internalRate || 0}</span> CHF</td>}
                {canManageTeam && (
                  <td className="cell-salary">
                    {salaryMap[member.employeeUuid]
                      ? <><span className="sensitive-bar">{new Intl.NumberFormat("de-CH").format(salaryMap[member.employeeUuid])}</span> CHF</>
                      : "-"}
                  </td>
                )}
                <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                  {canViewMember(member) && (
                    <button
                      className="btn-icon btn-auswertung"
                      onClick={(e) => handleOpenAuswertung(member, e)}
                      title="Auswertung öffnen"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3v18h18"/>
                        <path d="M7 14l4-4 4 4 5-5"/>
                      </svg>
                    </button>
                  )}
                  {canManageTeam && (
                    <>
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => {
                          setEditingMember(member);
                          setShowMemberModal(true);
                        }}
                        title="Bearbeiten"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(member.teamId)}
                        title="Löschen"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {filteredMembers.length === 0 && (
              <tr>
                <td colSpan={canManageTeam ? 9 : 7} className="no-results">
                  Keine Teammitglieder gefunden
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* FAB Add Button */}
      {canManageTeam && (
        <div className="team-fab">
          <button
            className="fab-add-button"
            onClick={() => {
              setEditingMember(null);
              setShowMemberModal(true);
            }}
            title="Neues Teammitglied hinzufügen"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        </div>
      )}

      {/* Add/Edit Member Modal */}
      {showMemberModal && (
        <TeamMemberModal
          member={editingMember}
          employees={employees}
          projects={projects}
          onSave={handleSaveMember}
          onClose={() => {
            setShowMemberModal(false);
            setEditingMember(null);
          }}
          onImageDelete={async (teamId, bildId) => {
            await deleteTeamImage(teamId, bildId);
            if (editingMember) {
              setEditingMember({ ...editingMember, bildId: "", bildUrls: null });
            }
          }}
          loading={loading}
          canManageTeam={canManageTeam}
        />
      )}

      {/* Person Detail Modal */}
      {showDetailModal && selectedMember && (
        <TeamDetailModal
          member={selectedMember}
          user={user}
          canManageTeam={canManageTeam}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedMember(null);
            // Salaries or PK contributions may have changed in the modal
            if (isAdmin) loadSalaryMap(teamMembers);
          }}
        />
      )}

      {/* HR Settings Modal */}
      {showHrModal && (
        <HrSettingsModal onClose={() => setShowHrModal(false)} />
      )}

      {/* Insurance Settings Modal */}
      {showInsuranceModal && (
        <InsuranceSettingsModal
          onClose={() => setShowInsuranceModal(false)}
          onRatesChanged={() => {
            // Recalculate insurance budget when rates change
            getAllActiveRates(new Date().toISOString().split("T")[0]).then((rates) => {
              if (rates) {
                recalculateInsuranceBudget(rates).catch(() => {});
                recalculateFAKBudget(rates.fak).catch(() => {});
              }
            }).catch(() => {});
            // PK mode may have been switched (Formel <-> Manuell)
            if (isAdmin) loadPkStatus(teamMembers, currentSalaries);
          }}
        />
      )}
    </div>
  );
};

export default TeamManagement;
